import { SvgIconProps, Typography, TypographyProps } from "@mui/material";
import React, { ComponentPropsWithoutRef } from "react";
import styles from "./card.module.css";

export type CoreCardProps = {
	title?: React.ReactNode;
	description?: React.ReactNode;
	titleAriaLabelledBy?: string;
	// eslint-disable-next-line deprecation/deprecation
	icon?:
		| React.FunctionComponent<React.SVGProps<SVGSVGElement>>
		| React.ReactNode
		| ((props: SvgIconProps) => JSX.Element);
	children?: React.ReactNode;
	useHeader?: boolean;
	style?: React.CSSProperties;
	headerFontSize?: string | number;
	fontSize?: string | number;
	boxShadow?: boolean;
	iconStroke?: boolean;
	className?: string;
	rightTitleContent?: React.ReactNode;
	rightContent?: React.ReactNode;
	slotProps?: {
		bodyWrapper?: Omit<ComponentPropsWithoutRef<"div">, "children">;
		titleWrapper?: Omit<ComponentPropsWithoutRef<"div">, "children">;
		iconWrapper?: Omit<ComponentPropsWithoutRef<"div">, "children">;
		title?: Omit<TypographyProps, "children">;
		description?: Omit<TypographyProps, "children">;
	};
};

export const CoreCard = (props: CoreCardProps) => {
	const title = (props.title || props.icon) && (
		<div
			className={`${props.useHeader ? styles.cardTitleHeader : styles.cardTitle} ${props.slotProps?.titleWrapper?.className || ""}`}
			style={{ fontSize: props.headerFontSize, ...props.slotProps?.titleWrapper?.style }}
			{...props.slotProps?.titleWrapper}
		>
			{props.icon && (
				<div
					className={`${props.useHeader ? styles.cardTitleIconHeader : styles.cardTitleIcon} ${
						props.iconStroke ? styles.strokeColor : ""
					} ${props.slotProps?.iconWrapper?.className || ""}`}
					style={{ width: props.headerFontSize, height: props.headerFontSize, ...props.slotProps?.iconWrapper?.style }}
					{...props.slotProps?.iconWrapper}
				>
					{typeof props.icon !== "function" ? (
						props.icon
					) : (
						<props.icon style={{ width: props.headerFontSize, height: props.headerFontSize }} />
					)}
				</div>
			)}
			<div className={styles.cardTitleRightContent}>
				{props.title && (
					<Typography
						variant={props.slotProps?.title?.variant ?? "h2"}
						className={`${styles.cardTitleText} ${props.slotProps?.title?.className || ""}`}
						fontSize={props.headerFontSize ?? "18px"}
						id={props.titleAriaLabelledBy}
						{...props.slotProps?.title}
					>
						{props.title}
					</Typography>
				)}
				{props.rightTitleContent}
			</div>
		</div>
	);

	const bodyWrapperClassNames = [styles.cardBodyWrapper];

	if (props.useHeader) bodyWrapperClassNames.push(styles.bodyWrapperUseHeader);
	if (props.slotProps?.bodyWrapper?.className) bodyWrapperClassNames.push(props.slotProps.bodyWrapper.className);

	const bodyWrapperClassName = bodyWrapperClassNames.join(" ");

	const mainContent = (
		<>
			{props.useHeader && <div className={styles.cardHeader}>{title}</div>}
			{((title && !props.useHeader) || props.description) && (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "6px",
						...(props.useHeader ? { padding: "0 15px" } : {}),
					}}
				>
					{!props.useHeader && title}
					{props.description && (
						<Typography
							{...props.slotProps?.description}
							className={`${styles.cardDescription} ${props.slotProps?.description?.className || ""}`}
							fontSize={props.slotProps?.description?.fontSize ?? props.fontSize}
						>
							{props.description}
						</Typography>
					)}
				</div>
			)}
			{props.children && (
				<div
					style={{
						fontSize: props.fontSize,
						...props.slotProps?.bodyWrapper?.style,
					}}
					{...props.slotProps?.bodyWrapper}
					className={bodyWrapperClassName}
				>
					{props.children}
				</div>
			)}
		</>
	);

	const cardClassNames = [styles.card];

	if (props.useHeader) cardClassNames.push(styles.cardUseHeader);
	if (props.className) cardClassNames.push(props.className);

	const cardClassName = cardClassNames.join(" ");

	return (
		<div
			className={cardClassName}
			style={{
				...(props.style ?? {}),
				boxShadow: props.boxShadow ? "0 0 5px 0 rgb(0 0 0/20%)" : undefined,
				border: props.boxShadow ? "none" : undefined,
			}}
		>
			{props.rightContent ? (
				<div className={styles.rightContentWrapper}>
					{mainContent}
					{props.rightContent}
				</div>
			) : (
				mainContent
			)}
		</div>
	);
};
